// src/components/features/PreActivitySelector/PreActivitySelector.jsx
// Activity Sequence Builder UI: select activities in a dropdown (multi-select),
// arrange them via drag-and-drop into sequential slots, and confirm.
import React, { useMemo, useState, useRef, useEffect } from "react";
import "./PreActivitySelector.css";
import { usePreActivitySelector } from "./hooks/usePreActivitySelector";

const PreActivitySelector = () => {
  const {
    objectTypes,
    selectedObjectType,
    activities,
    selectedActivities,
    availableTiles,
    sequence,
    isConfirmed,
    confirmedSequence,
    saveResponse,
    error,
    handleObjectTypeChange,
    handleActivitiesChange,
    handleDragStart,
    handleDrop,
    handleDragOver,
    removeFromSlot,
    handleConfirm,
    toggleActivitySelection,
  } = usePreActivitySelector();

  // Local state to control the custom dropdown (activities) open/close
  const [isActivitiesOpen, setIsActivitiesOpen] = useState(false);
  const activitiesRef = useRef(null);

  // Close the dropdown on outside click
  useEffect(() => {
    const handleDocClick = (e) => {
      if (!activitiesRef.current) return;
      if (!activitiesRef.current.contains(e.target)) {
        setIsActivitiesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  const allPlaced = useMemo(() => {
    const placed = new Set(sequence.flat());
    return selectedActivities.length > 0 && selectedActivities.every((a) => placed.has(a));
  }, [sequence, selectedActivities]);

  // New: minimal placement required (at least one placed)
  const hasAnyPlaced = useMemo(() => new Set(sequence.flat()).size > 0, [sequence]);

  return (
    <div className="activity-sequence-builder">
      <h2>Define Activity Sequence</h2>

      {/* Dropdowns */}
      <div className="selector">
        <label htmlFor="object-type-select">Object Type:</label>
        <select id="object-type-select" value={selectedObjectType} onChange={handleObjectTypeChange} aria-label="Select object type">
          <option value="">-- Select Object Type --</option>
          {objectTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {activities.length > 0 && (
        <div className="selector" ref={activitiesRef}>
          <label htmlFor="activities-toggle">Activities (click to select/unselect inside dropdown):</label>
          {/* Custom multi-select dropdown for click-to-toggle behavior */}
          <div className="multi-select" role="combobox" aria-expanded={isActivitiesOpen} aria-haspopup="listbox" aria-controls="activities-listbox">
            <button
              id="activities-toggle"
              type="button"
              className="multi-select-toggle"
              onClick={() => setIsActivitiesOpen((o) => !o)}
              aria-controls="activities-listbox"
            >
              {selectedActivities.length === 0 ? (
                <span className="placeholder">Select activities</span>
              ) : (
                <span>{selectedActivities.length} selected</span>
              )}
              <span className={`chevron ${isActivitiesOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
            </button>
            {isActivitiesOpen && (
              <div className="multi-select-panel" role="listbox" id="activities-listbox" aria-multiselectable="true">
                {activities.map((act) => {
                  const isSelected = selectedActivities.includes(act);
                  return (
                    <div key={act} className={`multi-select-option${isSelected ? ' selected' : ''}`} role="option" aria-selected={isSelected} onClick={() => toggleActivitySelection(act)}>
                      <input type="checkbox" readOnly checked={isSelected} tabIndex={-1} />
                      <span className="label">{act}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Area 1 – Activity Tiles (selected but not yet placed) */}
      {selectedActivities.length > 0 && (
        <div className="available-activities">
          <h3>Area 1: Activity Tiles</h3>
          <div className="activity-list">
            {availableTiles.length === 0 && (
              <span className="empty-slot">All selected activities have been placed.</span>
            )}
            {availableTiles.map((act) => (
              <div
                key={act}
                className="activity-tile"
                draggable
                onDragStart={(e) => handleDragStart(e, act, null)}
                title="Drag to sequence"
              >
                {act}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Area 2 – Sequence Fields */}
      {selectedActivities.length > 0 && (
        <div className="sequence-builder">
          <h3>Area 2: Sequence Fields</h3>
          <p style={{ margin: '6px 0 12px', color: '#555' }}>
            Tip: To create a parallel step, drop multiple activities into the same numbered field. Steps 1, 2, 3, 4 are sequential; items within a single step run in parallel.
          </p>
          <div className="sequence-slots">
            {sequence.map((slot, index) => (
              <div
                key={index}
                className="sequence-slot"
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => e.currentTarget.classList.add("drag-over")}
                onDragLeave={(e) => e.currentTarget.classList.remove("drag-over")}
              >
                <span className="slot-index">{index + 1}</span>
                {slot.length === 0 ? (
                  <span className="empty-slot">Drop here</span>
                ) : (
                  <>
                    {slot.length > 1 && (
                      <div
                        className="parallel-badge"
                        style={{
                          fontSize: 12,
                          color: '#1f6feb',
                          background: '#e7f1ff',
                          border: '1px solid #c6e2ff',
                          borderRadius: 4,
                          padding: '2px 6px',
                          display: 'inline-block',
                          marginBottom: 6
                        }}
                        title="Multiple activities in this step will run in parallel"
                      >
                        Parallel
                      </div>
                    )}
                    {slot.map((act) => (
                      <div
                        key={act}
                        className="activity-tile"
                        draggable
                        onDragStart={(e) => handleDragStart(e, act, index)}
                        title="Drag to another slot or remove"
                      >
                        {act}
                        <button
                          className="remove-btn"
                          onClick={(ev) => { ev.stopPropagation(); removeFromSlot(index, act); }}
                          aria-label={`Remove ${act} from slot ${index + 1}`}
                          title="Remove from this slot"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Confirmation */}
      {selectedActivities.length > 0 && (
        <div className="sequence-controls">
          <button
            className={`confirm-btn${!hasAnyPlaced ? " disabled" : ""}`}
            onClick={handleConfirm}
            disabled={!hasAnyPlaced}
            title={!hasAnyPlaced ? "Place at least one activity to submit" : "Submit sequence (partial allowed)"}
          >
            Confirm Sequence
          </button>
        </div>
      )}

      {isConfirmed && (
        <div className="confirmation-msg" style={{ marginTop: 12 }}>
          Sequence submitted! Parallel steps are supported. Partial submission allowed.
          {saveResponse && (
            <pre style={{ marginTop: 8, background: '#f6f8fa', padding: 8, borderRadius: 4 }}>
{JSON.stringify(saveResponse.sequenceDict, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default PreActivitySelector;
